import { useEffect, useMemo, useState } from "react";
import { Form, Col, Row, Button } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import { CheckCircleOutlined, CloseOutlined } from "@ant-design/icons";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import {
  oldTypeOptions,
  newTypeOptions,
  booleanOptions,
} from "../../../utils/Common";

const GIFT_CARDS_BASE = `${process.env.REACT_APP_API_URL}/api/giftcards`;

/** Door + device SKUs: raw counts in the form match the API (1–255 or 1–65535). */
const NUMERIC_DEVICE_ITEM_IDS = [3, 202, 203, 204, 205, 206, 207, 208];
/** Mobile Credential: form uses UI scale (×100); API receives value / 100 (same as MySite / OptionCards). */
const MOBILE_CREDENTIAL_ITEM_ID = 209;
const PER_UNIT_LICENSE_ITEM_IDS = [
  ...NUMERIC_DEVICE_ITEM_IDS,
  MOBILE_CREDENTIAL_ITEM_ID,
];
/** Door (3) and Directory (208) allow up to 65535; others max 255. */
const DEVICE_CODE_HIGH_CAP_ITEMS = new Set([3, 208]);

const MOBILE_CREDENTIAL_UI_MIN = 100;
const MOBILE_CREDENTIAL_UI_MAX = 25500;
const MOBILE_CREDENTIAL_UI_STEP = 100;

function getDeviceCodeRange(itemId) {
  const n = Number(itemId);
  if (DEVICE_CODE_HIGH_CAP_ITEMS.has(n)) return { min: 0, max: 65535 };
  return { min: 0, max: 255 };
}

function deviceCodeBoundsMessage(min, max) {
  return `Please enter a number between ${min} and ${max}.`;
}

function buildMobileCredentialQuantityOptions() {
  const options = [];
  for (
    let v = MOBILE_CREDENTIAL_UI_MIN;
    v <= MOBILE_CREDENTIAL_UI_MAX;
    v += MOBILE_CREDENTIAL_UI_STEP
  ) {
    options.push({ label: String(v), value: v });
  }
  return options;
}

function mobileCredentialBoundsMessage() {
  return `Choose a value from ${MOBILE_CREDENTIAL_UI_MIN.toLocaleString()} to ${MOBILE_CREDENTIAL_UI_MAX.toLocaleString()} in steps of ${MOBILE_CREDENTIAL_UI_STEP}.`;
}

export const PrepurchasedLicenseItemForm = ({
  giftCardId,
  editingItem,
  editingItemNo,
  existingItems = [],
  onCancel,
  onSaved,
}) => {
  const toValidNumber = (value) => {
    if (value === null || value === undefined || value === "") return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const normalizeTypeToken = (v) =>
    String(v || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/-/g, "");

  const parseOldTypeValue = (raw) => {
    const num = toValidNumber(raw);
    if (num !== undefined) return num;
    const token = normalizeTypeToken(raw);
    const map = {
      none: 0,
      equal: 1,
      equalto: 1,
      lessthan: 2,
      greaterthan: 3,
    };
    return map[token];
  };

  const parseNewTypeValue = (raw) => {
    const num = toValidNumber(raw);
    if (num !== undefined) return num;
    const token = normalizeTypeToken(raw);
    const map = {
      replace: 0,
      add: 1,
    };
    return map[token];
  };

  const parseBooleanOptionValue = (raw, label) => {
    const parsed = toValidNumber(raw);
    if (parsed === 0 || parsed === 1) return parsed;
    const token = normalizeTypeToken(label ?? raw);
    if (token === "yes") return 1;
    if (token === "no") return 0;
    return parsed;
  };

  const [selectedItem, setSelectedItem] = useState(null);
  const [form] = Form.useForm();
  const selectedOldCode = Form.useWatch("OldCode", form);
  const selectedOldType = Form.useWatch("OldType", form);
  const selectedNewType = Form.useWatch("NewType", form);

  const postUrl = giftCardId && `${GIFT_CARDS_BASE}/${giftCardId}/items/`;
  const resolvedEditItemNo = editingItemNo ?? editingItem?.ItemNo;
  const putItemUrl =
    giftCardId && resolvedEditItemNo != null
      ? `${GIFT_CARDS_BASE}/${giftCardId}/items/${resolvedEditItemNo}/`
      : null;
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/getoptions/`;
  const isNumericDeviceItem =
    selectedItem != null &&
    NUMERIC_DEVICE_ITEM_IDS.includes(Number(selectedItem));
  const isMobileCredentialItem =
    Number(selectedItem) === MOBILE_CREDENTIAL_ITEM_ID;
  const isPerUnitLicenseItem =
    selectedItem != null &&
    PER_UNIT_LICENSE_ITEM_IDS.includes(Number(selectedItem));
  const isLicenseOptionItem =
    selectedItem != null &&
    Number(selectedItem) >= 101 &&
    Number(selectedItem) <= 108;

  const licenseUrl =
    selectedItem != null && !isPerUnitLicenseItem
      ? `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/?limit=100&sort_by=Item&order=asc&page=1&Item=${selectedItem}`
      : null;

  const {
    data: optionData,
    loading: optionDataLoading,
    refetch: optionDataRefetch,
  } = useGet(optionUrl);

  const { data: licenseOptionData, loading: licenseOptionDataLoading } =
    useGet(licenseUrl);

  const licenseOptions = licenseOptionData?.data
    ?.filter((item) => !(selectedItem === 1 && [3, 4].includes(item.Code)))
    .map((item) => ({
      label: item.CodeName,
      value: item.Code,
    }));

  const hideOldLicense =
    Boolean(selectedItem) &&
    !isPerUnitLicenseItem &&
    !isLicenseOptionItem &&
    !licenseOptionDataLoading &&
    Array.isArray(licenseOptions) &&
    licenseOptions.length <= 1;

  const oldCodeNum = Number(selectedOldCode);
  /**
   * New license options relative to Old License.
   * - Default: strictly greater than old (upgrade only: Old Code < New Code)
   * - When Old Type is "Less Than" (2) for Site Type or Model (items 1, 2):
   *   show all options (no filtering) so user can pick any tier.
   */
  const newLicenseOptions = hideOldLicense
    ? licenseOptions
    : licenseOptions?.filter((opt) => {
        const codeNum = Number(opt.value);
        if (
          selectedOldCode === undefined ||
          selectedOldCode === null ||
          selectedOldCode === ""
        ) {
          return true;
        }
        if (!Number.isFinite(oldCodeNum) || !Number.isFinite(codeNum)) {
          return true;
        }
        const oldTypeNum = toValidNumber(selectedOldType);
        const itemNum = toValidNumber(selectedItem);
        if (oldTypeNum === 2 && (itemNum === 1 || itemNum === 2)) {
          // Less Than for Site Type / Model: no filtering.
          return true;
        }
        return codeNum > oldCodeNum;
      });

  const filteredOldLicenseOptions = useMemo(() => {
    if (!Array.isArray(licenseOptions) || licenseOptions.length === 0) {
      return licenseOptions;
    }
    const numericValues = licenseOptions
      .map((opt) => Number(opt.value))
      .filter((n) => Number.isFinite(n));
    if (numericValues.length === 0) {
      return licenseOptions;
    }
    const min = Math.min(...numericValues);
    const max = Math.max(...numericValues);
    const ot = toValidNumber(selectedOldType);
    if (ot === 2) {
      // Less Than: exclude the smallest tier so a strictly smaller value exists.
      return licenseOptions.filter((opt) => Number(opt.value) > min);
    }
    if (ot === 3) {
      // Greater Than: exclude the largest tier so a strictly larger value exists.
      return licenseOptions.filter((opt) => Number(opt.value) < max);
    }
    return licenseOptions;
  }, [licenseOptions, selectedOldType]);

  const filteredLicenseOptionNewBooleanOptions = useMemo(() => {
    const oldTypeNum = toValidNumber(selectedOldType);
    if (isLicenseOptionItem && oldTypeNum === 1) {
      return booleanOptions.filter((opt) => Number(opt.value) === 1);
    }
    return booleanOptions;
  }, [isLicenseOptionItem, selectedOldType]);

  const modifiedItems = getModifiedResponse(optionData, "items");

  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);

  const putHookUrl =
    putItemUrl ??
    (giftCardId ? `${GIFT_CARDS_BASE}/${giftCardId}/items/0/` : "");
  const {
    putData: putAction,
    loading: putLoading,
    error: putError,
    data: putData,
  } = usePut(putHookUrl);

  const isEdit = resolvedEditItemNo != null;
  const saving = postLoading || putLoading;
  const editingItemValue =
    toValidNumber(editingItem?.Item) ??
    toValidNumber(editingItem?.LicenseItem) ??
    modifiedItems?.find(
      (option) => String(option?.label) === String(editingItem?.LicenseItem),
    )?.value;
  const usedItemValues = new Set(
    (existingItems ?? [])
      .map((item) => {
        const numericValue =
          toValidNumber(item?.Item) ?? toValidNumber(item?.LicenseItem);
        if (numericValue != null) return numericValue;
        const matchedOption = (modifiedItems ?? []).find(
          (option) => String(option?.label) === String(item?.LicenseItem),
        );
        return matchedOption?.value;
      })
      .filter((value) => value != null),
  );
  const filteredItemOptions = (modifiedItems ?? []).filter((option) => {
    if (isEdit && option.value === editingItemValue) return true;
    return !usedItemValues.has(option.value);
  });

  const deviceItemLabel =
    modifiedItems?.find((o) => Number(o.value) === Number(selectedItem))
      ?.label ?? "Device";

  const deviceCodeRange = isNumericDeviceItem
    ? getDeviceCodeRange(selectedItem)
    : null;

  const mobileCredentialQuantityOptions = useMemo(
    () => buildMobileCredentialQuantityOptions(),
    [],
  );

  const ITEM_TYPE_RULES = {
    // 1: Site Type
    1: { old: [0, 1, 2, 3], new: [0] },
    // 2: Model
    2: { old: [0, 1, 2, 3], new: [0] },
    // 3: Door
    3: { old: [0, 1, 2, 3], new: [0, 1] },
    // 101–108: Options rows (Old=Don't Care/Equal To, New=Replace)
    101: { old: [0, 1], new: [0] },
    102: { old: [0, 1], new: [0] },
    103: { old: [0, 1], new: [0] },
    104: { old: [0, 1], new: [0] },
    105: { old: [0, 1], new: [0] },
    106: { old: [0, 1], new: [0] },
    107: { old: [0, 1], new: [0] },
    108: { old: [0, 1], new: [0] },
    // Channel + device‑type items (None|Equal|Less Than|Greater Than; New=Replace|Add)
    202: { old: [0, 1, 2, 3], new: [0, 1] }, // Channel
    203: { old: [0, 1, 2, 3], new: [0, 1] }, // Lockset
    205: { old: [0, 1, 2, 3], new: [0, 1] }, // OSDP Door
    206: { old: [0, 1, 2, 3], new: [0, 1] }, // Trilogy Lockset
    208: { old: [0, 1, 2, 3], new: [0, 1] }, // Unit
    209: { old: [0, 1, 2, 3], new: [0, 1] }, // Mobile Credential
  };

  const filteredOldTypeOptions = useMemo(() => {
    const itemNum = toValidNumber(selectedItem);
    const rule = itemNum != null ? ITEM_TYPE_RULES[itemNum] : null;
    const allowed = rule?.old;
    if (!Array.isArray(allowed) || allowed.length === 0) {
      return oldTypeOptions;
    }
    return oldTypeOptions.filter((opt) => allowed.includes(Number(opt.value)));
  }, [selectedItem]);

  const filteredNewTypeOptions = useMemo(() => {
    const itemNum = toValidNumber(selectedItem);
    const rule = itemNum != null ? ITEM_TYPE_RULES[itemNum] : null;
    const allowed = rule?.new;
    if (!Array.isArray(allowed) || allowed.length === 0) {
      return newTypeOptions;
    }
    return newTypeOptions.filter((opt) => allowed.includes(Number(opt.value)));
  }, [selectedItem]);

  const mobileCredentialOldFormItemProps = useMemo(
    () => ({
      dependencies: ["OldType"],
      rules: [
        {
          validator: (_, v) => {
            const oldType = toValidNumber(form.getFieldValue("OldType"));
            const isNone = oldType === 0 || oldType == null;
            // If OldType is None, field is effectively disabled/hidden; ignore value.
            if (isNone) {
              return Promise.resolve();
            }
            if (v === undefined || v === null || v === "") {
              return Promise.reject(new Error("Old License is required"));
            }
            const n = Number(v);
            if (
              !Number.isFinite(n) ||
              n < MOBILE_CREDENTIAL_UI_MIN ||
              n > MOBILE_CREDENTIAL_UI_MAX ||
              n % MOBILE_CREDENTIAL_UI_STEP !== 0
            ) {
              return Promise.reject(new Error(mobileCredentialBoundsMessage()));
            }
            return Promise.resolve();
          },
        },
      ],
    }),
    [deviceItemLabel, form],
  );

  const mobileCredentialNewFormItemProps = useMemo(
    () => ({
      dependencies: ["OldCode", "NewType"],
      rules: [
        {
          validator: (_, v) => {
            if (v === undefined || v === null || v === "") {
              return Promise.reject(new Error(`New License is required`));
            }
            const n = Number(v);
            if (
              !Number.isFinite(n) ||
              n < MOBILE_CREDENTIAL_UI_MIN ||
              n > MOBILE_CREDENTIAL_UI_MAX ||
              n % MOBILE_CREDENTIAL_UI_STEP !== 0
            ) {
              return Promise.reject(new Error(mobileCredentialBoundsMessage()));
            }
            const newTypeNum = toValidNumber(form.getFieldValue("NewType"));
            // For Add mode, allow entering any valid count.
            if (newTypeNum === 1) {
              return Promise.resolve();
            }
            const oldV = form.getFieldValue("OldCode");
            if (oldV !== undefined && oldV !== null && oldV !== "") {
              const oldN = Number(oldV);
              if (Number.isFinite(oldN) && n <= oldN) {
                return Promise.reject(
                  new Error(
                    "The new license count must be greater than the old license count.",
                  ),
                );
              }
            }
            return Promise.resolve();
          },
        },
      ],
    }),
    [deviceItemLabel, form],
  );

  const deviceOldRules = useMemo(() => {
    if (!deviceCodeRange) return [];
    const { min, max } = deviceCodeRange;
    const boundsMsg = deviceCodeBoundsMessage(min, max);
    return [
      {
        validator: (_, v) => {
          const oldType = toValidNumber(form.getFieldValue("OldType"));
          const isNone = oldType === 0 || oldType == null;
          if (isNone && (v === undefined || v === null || v === "")) {
            return Promise.resolve();
          }
          if (!isNone && (v === undefined || v === null || v === "")) {
            return Promise.reject(new Error("Old License is required"));
          }
          const n = Number(v);
          if (
            !Number.isFinite(n) ||
            !Number.isInteger(n) ||
            n < min ||
            n > max
          ) {
            return Promise.reject(new Error(boundsMsg));
          }
          return Promise.resolve();
        },
      },
    ];
  }, [deviceItemLabel, deviceCodeRange?.min, deviceCodeRange?.max, form]);

  const deviceNewRules = useMemo(() => {
    if (!deviceCodeRange) return [];
    const { min, max } = deviceCodeRange;
    const boundsMsg = deviceCodeBoundsMessage(min, max);
    return [
      {
        validator: (_, v) => {
          if (v === undefined || v === null || v === "") {
            return Promise.reject(new Error(`New License is required`));
          }
          const n = Number(v);
          if (
            !Number.isFinite(n) ||
            !Number.isInteger(n) ||
            n < min ||
            n > max
          ) {
            return Promise.reject(new Error(boundsMsg));
          }
          const newTypeNum = toValidNumber(form.getFieldValue("NewType"));
          // For Add mode, allow entering any valid count.
          if (newTypeNum === 1) {
            return Promise.resolve();
          }
          const oldV = form.getFieldValue("OldCode");
          if (oldV !== undefined && oldV !== null && oldV !== "") {
            const oldN = Number(oldV);
            if (Number.isFinite(oldN) && n <= oldN) {
              return Promise.reject(
                new Error(
                  "The new license count must be greater than the old license count.",
                ),
              );
            }
          }
          return Promise.resolve();
        },
      },
    ];
  }, [deviceItemLabel, deviceCodeRange?.min, deviceCodeRange?.max, form]);

  const newLicenseSelectFormItemProps = useMemo(() => {
    if (hideOldLicense) {
      return {};
    }
    return {
      dependencies: ["OldCode", "OldType", "NewType", "Item"],
      rules: [
        {
          validator: (_, value) => {
            if (value === undefined || value === null || value === "") {
              return Promise.reject(new Error("New License is required"));
            }
            const oldTypeNum = toValidNumber(form.getFieldValue("OldType"));
            const itemNum = toValidNumber(form.getFieldValue("Item"));
            // For "Less Than" comparisons on Model/Site Type (and generally), we don't enforce New > Old.
            if (oldTypeNum === 2 && (itemNum === 1 || itemNum === 2)) {
              return Promise.resolve();
            }
            const oldV = form.getFieldValue("OldCode");
            if (oldV === undefined || oldV === null || oldV === "") {
              return Promise.resolve();
            }
            if (Number(value) <= Number(oldV)) {
              return Promise.reject(
                new Error(
                  "The new license must be greater than the old license.",
                ),
              );
            }
            return Promise.resolve();
          },
        },
      ],
    };
  }, [hideOldLicense, form]);

  const onFinish = async (values) => {
    const licenseItem =
      toValidNumber(values.Item) ??
      toValidNumber(editingItem?.Item) ??
      toValidNumber(editingItem?.LicenseItem);
    const optCount = licenseOptions?.length ?? 0;
    const perUnitMode =
      licenseItem != null &&
      PER_UNIT_LICENSE_ITEM_IDS.includes(Number(licenseItem));
    const booleanOptionMode =
      licenseItem != null &&
      Number(licenseItem) >= 101 &&
      Number(licenseItem) <= 108;
    const mobileCredentialMode =
      Number(licenseItem) === MOBILE_CREDENTIAL_ITEM_ID;
    const singleOrNoLicenseOption =
      !perUnitMode &&
      !booleanOptionMode &&
      Array.isArray(licenseOptions) &&
      optCount <= 1;
    const oldTypeNum = toValidNumber(values.OldType);
    const oldTypeIsNone = oldTypeNum === 0 || oldTypeNum == null;
    let oldCode = singleOrNoLicenseOption
      ? null
      : (toValidNumber(values.OldCode) ?? null);
    if (oldTypeIsNone) {
      oldCode = null;
    }
    let newCode = toValidNumber(values.NewCode);
    if (mobileCredentialMode) {
      if (oldCode != null) oldCode = oldCode / 100;
      if (newCode != null) newCode = newCode / 100;
    }
    const modifiedValue = {
      LicenseItem: licenseItem,
      OldCode: oldCode,
      NewCode: newCode ?? null,
      OldType: oldTypeNum ?? null,
      NewType: toValidNumber(values.NewType) ?? null,
    };
    try {
      if (isEdit) {
        await putAction(modifiedValue);
      } else {
        await postAction(modifiedValue);
      }
    } catch (err) {
      openErrorNotification("Something went wrong.");
      console.log(err);
    }
    form.resetFields();
  };

  useEffect(() => {
    optionDataRefetch();
  }, []);

  useEffect(() => {
    form.resetFields();
    if (editingItem) {
      setSelectedItem(editingItemValue);
      const editIsMobile209 =
        Number(editingItemValue) === MOBILE_CREDENTIAL_ITEM_ID;
      const itemNum = toValidNumber(editingItemValue);
      const editIsLicenseOptionItem =
        itemNum != null && itemNum >= 101 && itemNum <= 108;
      const rawOld = editIsLicenseOptionItem
        ? parseBooleanOptionValue(
            editingItem?.OldCode,
            editingItem?.OldCodeLabel,
          )
        : toValidNumber(editingItem?.OldCode);
      const rawNew = editIsLicenseOptionItem
        ? parseBooleanOptionValue(
            editingItem?.NewCode,
            editingItem?.NewCodeLabel,
          )
        : toValidNumber(editingItem?.NewCode);
      const rule = itemNum != null ? ITEM_TYPE_RULES[itemNum] : null;
      const defaultOldType =
        (rule?.old && rule.old[0] != null ? rule.old[0] : 0) ?? 0;
      const defaultNewType =
        (rule?.new && rule.new[0] != null ? rule.new[0] : 0) ?? 0;

      const prefOldType =
        editingItem?.OldType !== undefined && editingItem?.OldType !== null
          ? parseOldTypeValue(editingItem.OldType)
          : undefined;
      const prefNewType =
        editingItem?.NewType !== undefined && editingItem?.NewType !== null
          ? parseNewTypeValue(editingItem.NewType)
          : undefined;

      form.setFieldsValue({
        Item: editingItemValue,
        OldCode: editIsMobile209 && rawOld != null ? rawOld * 100 : rawOld,
        NewCode: editIsMobile209 && rawNew != null ? rawNew * 100 : rawNew,
        OldType:
          prefOldType !== undefined && prefOldType !== null
            ? prefOldType
            : defaultOldType,
        NewType:
          prefNewType !== undefined && prefNewType !== null
            ? prefNewType
            : defaultNewType,
      });
    } else {
      setSelectedItem(null);
    }
  }, [editingItem, editingItemValue, form]);

  useEffect(() => {
    if (isEdit) return;
    if (hideOldLicense) {
      form.setFieldsValue({ OldCode: undefined });
    }
  }, [hideOldLicense, isEdit, form]);

  useEffect(() => {
    if (isEdit) return;
    const oldTypeNum = toValidNumber(selectedOldType);
    if (!isLicenseOptionItem || oldTypeNum !== 1) {
      return;
    }
    const currentOldCode = form.getFieldValue("OldCode");
    if (
      currentOldCode === undefined ||
      currentOldCode === null ||
      currentOldCode === ""
    ) {
      form.setFieldsValue({ OldCode: 0 });
    }
  }, [isLicenseOptionItem, selectedOldType, isEdit, form]);

  /** License options: default New License to Yes (1) when unset; 0 stays No. */
  useEffect(() => {
    if (isEdit) return;
    if (!isLicenseOptionItem) return;
    const newV = form.getFieldValue("NewCode");
    if (newV !== undefined && newV !== null && newV !== "") return;
    form.setFieldsValue({ NewCode: 1 });
  }, [isLicenseOptionItem, selectedItem, isEdit, form]);

  useEffect(() => {
    if (isEdit) return;
    const oldTypeNum = toValidNumber(selectedOldType);
    if (!isLicenseOptionItem || oldTypeNum !== 1) return;
    const newV = toValidNumber(form.getFieldValue("NewCode"));
    if (newV === 0) {
      form.setFieldsValue({ NewCode: 1 });
    }
  }, [isLicenseOptionItem, selectedOldType, isEdit, form]);

  const soleNewLicenseCode =
    hideOldLicense &&
    Array.isArray(licenseOptions) &&
    licenseOptions.length === 1
      ? licenseOptions[0]?.value
      : undefined;

  useEffect(() => {
    if (isEdit) return;
    if (soleNewLicenseCode === undefined || soleNewLicenseCode === null) {
      return;
    }
    const current = form.getFieldValue("NewCode");
    if (current != null && current !== "") {
      return;
    }
    form.setFieldsValue({ NewCode: soleNewLicenseCode });
  }, [soleNewLicenseCode, isEdit, form]);

  useEffect(() => {
    if (isEdit) return;
    if (!isPerUnitLicenseItem) return;
    const newTypeNum = toValidNumber(selectedNewType);
    if (newTypeNum === 1) return;
    const newV = form.getFieldValue("NewCode");
    if (
      selectedOldCode === undefined ||
      selectedOldCode === null ||
      selectedOldCode === ""
    ) {
      return;
    }
    if (newV === undefined || newV === null || newV === "") return;
    const oldN = Number(selectedOldCode);
    const newN = Number(newV);
    if (!Number.isFinite(oldN) || !Number.isFinite(newN)) return;
    if (newN <= oldN) {
      form.setFieldsValue({ NewCode: undefined });
    }
  }, [selectedOldCode, selectedNewType, isPerUnitLicenseItem, isEdit, form]);

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postLoading && !postError && postData && !isEdit) {
      onSaved?.();
      onCancel();
      openConfirmNotification("Success");
    }
  }, [postError, postData, postLoading, isEdit, onCancel, onSaved]);

  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    } else if (!putLoading && !putError && putData && isEdit) {
      onSaved?.();
      onCancel();
      openConfirmNotification("Success");
    }
  }, [putError, putData, putLoading, isEdit, onCancel, onSaved]);

  return (
    <>
      <Form
        validateTrigger={"onChange"}
        validateFirst
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout={"vertical"}
        autoComplete={"off"}
      >
        <div className="px-2 pt-0 pb-3">
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <CustomSelect
                label="License Item"
                name="Item"
                options={filteredItemOptions}
                placeholder="Please select"
                required
                loading={optionDataLoading}
                onChange={(value) => {
                  setSelectedItem(value);
                  const numericValue = toValidNumber(value);
                  const rule =
                    numericValue != null ? ITEM_TYPE_RULES[numericValue] : null;
                  const defaultOldType =
                    (Array.isArray(rule?.old) && rule.old[0] != null
                      ? rule.old[0]
                      : 0) ?? 0;
                  const defaultNewType =
                    (Array.isArray(rule?.new) && rule.new[0] != null
                      ? rule.new[0]
                      : 0) ?? 0;
                  const isLicenseOption =
                    numericValue != null &&
                    numericValue >= 101 &&
                    numericValue <= 108;
                  form.setFieldsValue({
                    OldCode: isLicenseOption ? 0 : undefined,
                    NewCode: isLicenseOption ? 1 : undefined,
                    OldType: defaultOldType,
                    NewType: defaultNewType,
                  });
                }}
              />
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <CustomSelect
                label="Old Type"
                name="OldType"
                options={filteredOldTypeOptions}
                placeholder="Please select"
                disabled={!selectedItem}
                allowClear={false}
                onChange={(value) => {
                  const num = toValidNumber(value);
                  if (num === 0 || num == null) {
                    form.setFieldsValue({ OldCode: undefined });
                  }
                }}
              />
            </Col>
            {isMobileCredentialItem ? (
              <>
                {!(
                  toValidNumber(selectedOldType) === 0 ||
                  selectedOldType == null
                ) ? (
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <CustomSelect
                      label={`Old License`}
                      name="OldCode"
                      options={mobileCredentialQuantityOptions}
                      placeholder="Please select"
                      disabled={!selectedItem}
                      formItemProps={mobileCredentialOldFormItemProps}
                      required
                      onChange={(value) => {
                        const newTypeNum = toValidNumber(
                          form.getFieldValue("NewType"),
                        );
                        if (newTypeNum === 1) {
                          return;
                        }
                        const currentNew = form.getFieldValue("NewCode");
                        if (
                          currentNew == null ||
                          currentNew === "" ||
                          value == null ||
                          value === ""
                        ) {
                          return;
                        }
                        const oldN = Number(value);
                        const newN = Number(currentNew);
                        if (!Number.isFinite(oldN) || !Number.isFinite(newN))
                          return;
                        if (newN <= oldN) {
                          form.setFieldsValue({ NewCode: undefined });
                        }
                      }}
                    />
                  </Col>
                ) : null}
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <CustomSelect
                    label="New Type"
                    name="NewType"
                    options={filteredNewTypeOptions}
                    placeholder="Please select"
                    disabled={!selectedItem}
                    allowClear={false}
                  />
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <CustomSelect
                    label={`New License`}
                    name="NewCode"
                    options={mobileCredentialQuantityOptions}
                    placeholder="Please select"
                    required
                    disabled={!selectedItem}
                    formItemProps={mobileCredentialNewFormItemProps}
                  />
                </Col>
              </>
            ) : isNumericDeviceItem && deviceCodeRange ? (
              <>
                {!(
                  toValidNumber(selectedOldType) === 0 ||
                  selectedOldType == null
                ) ? (
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <CustomInput
                      label={`Old License`}
                      name="OldCode"
                      type="number"
                      rules={deviceOldRules}
                      disable={!selectedItem}
                      min={deviceCodeRange.min}
                      max={deviceCodeRange.max}
                      step={1}
                      required
                    />
                  </Col>
                ) : null}
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <CustomSelect
                    label="New Type"
                    name="NewType"
                    options={filteredNewTypeOptions}
                    placeholder="Please select"
                    disabled={!selectedItem}
                    allowClear={false}
                  />
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <CustomInput
                    label={`New License`}
                    name="NewCode"
                    type="number"
                    required
                    rules={deviceNewRules}
                    dependencies={["OldCode", "NewType"]}
                    disable={!selectedItem}
                    min={deviceCodeRange.min}
                    max={deviceCodeRange.max}
                    step={1}
                  />
                </Col>
              </>
            ) : isLicenseOptionItem ? (
              <>
                {toValidNumber(selectedOldType) === 1 ? (
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <CustomSelect
                      label="Old License"
                      name="OldCode"
                      options={booleanOptions}
                      placeholder="Please select"
                      disabled={!selectedItem}
                      required
                    />
                  </Col>
                ) : null}
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <CustomSelect
                    label="New Type"
                    name="NewType"
                    options={filteredNewTypeOptions}
                    placeholder="Please select"
                    disabled={!selectedItem}
                    allowClear={false}
                  />
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <CustomSelect
                    label="New License"
                    name="NewCode"
                    options={filteredLicenseOptionNewBooleanOptions}
                    placeholder="Please select"
                    required
                    disabled={!selectedItem}
                  />
                </Col>
              </>
            ) : (
              <>
                {!hideOldLicense &&
                !(
                  toValidNumber(selectedOldType) === 0 ||
                  selectedOldType == null
                ) ? (
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <CustomSelect
                      label="Old License"
                      name="OldCode"
                      options={filteredOldLicenseOptions}
                      placeholder="Please select"
                      loading={licenseOptionDataLoading}
                      disabled={!selectedItem}
                      required
                      onChange={(value) => {
                        const newTypeNum = toValidNumber(
                          form.getFieldValue("NewType"),
                        );
                        if (newTypeNum === 1) {
                          return;
                        }
                        const currentNew = form.getFieldValue("NewCode");
                        if (
                          currentNew == null ||
                          currentNew === "" ||
                          value == null ||
                          value === ""
                        ) {
                          return;
                        }
                        const oldN = Number(value);
                        const newN = Number(currentNew);
                        if (!Number.isFinite(oldN) || !Number.isFinite(newN))
                          return;
                        if (newN <= oldN) {
                          form.setFieldsValue({ NewCode: undefined });
                        }
                      }}
                    />
                  </Col>
                ) : null}
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <CustomSelect
                    label="New Type"
                    name="NewType"
                    options={filteredNewTypeOptions}
                    placeholder="Please select"
                    disabled={!selectedItem}
                    allowClear={false}
                  />
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <CustomSelect
                    label="New License"
                    name="NewCode"
                    options={newLicenseOptions}
                    placeholder="Please select"
                    required
                    loading={licenseOptionDataLoading}
                    disabled={!selectedItem}
                    formItemProps={newLicenseSelectFormItemProps}
                  />
                </Col>
              </>
            )}
          </Row>
        </div>
      </Form>

      <div className="flex justify-center space-x-2 mt-2.5">
        <Button
          type="primary"
          className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center "
          onClick={() => form.submit()}
          loading={saving}
          disabled={saving}
        >
          <CheckCircleOutlined className="!text-[14px] font-medium" />
          <span className="">Apply</span>
        </Button>
        <Button className="reset-button" onClick={onCancel}>
          <CloseOutlined />
          <span className="">Cancel</span>
        </Button>
      </div>
    </>
  );
};
